import { FormEvent, useEffect, useState } from "react";

/**
 * Public account-deletion page.
 *
 * Linked from the Google Play Console's "Data safety" section, so it must be
 * reachable without installing the app and without an existing web session
 * (Google Play's account/data deletion policy). It self-serves deletion with
 * the IAM service's existing endpoints only: the user proves their identity
 * by signing in (POST /auth/login), then the page calls the same
 * DELETE /auth/delete the mobile app uses. The token is held in component
 * state for the single deletion call and never persisted.
 */

const IAM_URL = process.env.REACT_APP_IAM_URL;

const inputClass =
	"px-[14px] py-[10px] h-11 border border-pbg w-full rounded-md placeholder:text-base placeholder:text-ashShade-16 focus:outline-0 focus:border-blueShades-6";

type Step = "signin" | "confirm" | "deleted";

/** Calls the IAM service and throws the API's message on a non-2xx response
 *  (same longer-of-message/error convention as helpers/fetcher). */
const iamFetch = async (
	path: string,
	method: "POST" | "DELETE",
	body: object,
	token?: string
) => {
	const headers: Record<string, string> = {
		Accept: "application/json",
		"Content-Type": "application/json",
	};
	if (token) headers.Authorization = `Bearer ${token}`;

	const request = await fetch(`${IAM_URL}${path}`, {
		method,
		headers,
		body: JSON.stringify(body),
	});
	let response: any = {};
	try {
		response = await request.json();
	} catch {
		// Non-JSON body — fall through to the generic message below
	}
	if (!String(request.status).startsWith("2")) {
		const { message, error } = response || {};
		const longest =
			message && error
				? String(error).length > String(message).length
					? error
					: message
				: message || error;
		throw new Error(longest || "Something went wrong. Please try again.");
	}
	return response;
};

/** The IAM login response carries the account id inside the JWT payload. */
const userIdFromToken = (token: string): string => {
	try {
		const segment = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
		const padded = segment + "=".repeat((4 - (segment.length % 4)) % 4);
		return JSON.parse(window.atob(padded))._id || "";
	} catch {
		return "";
	}
};

const DeleteAccount = () => {
	const [step, setStep] = useState<Step>("signin");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [token, setToken] = useState("");
	const [userId, setUserId] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		const previousTitle = document.title;
		document.title = "Delete your account | Bankolé";
		return () => {
			document.title = previousTitle;
		};
	}, []);

	const handleSignIn = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!/\S+@\S+\.\S+/.test(email)) {
			setError("Please enter a valid email address.");
			return;
		}
		if (!password) {
			setError("Please enter your password.");
			return;
		}
		setLoading(true);
		setError("");
		try {
			const response = await iamFetch("/auth/login", "POST", {
				email: email.toLowerCase(),
				password,
			});
			const receivedToken = response?.data?.token;
			const id = receivedToken ? userIdFromToken(receivedToken) : "";
			if (!receivedToken || !id) {
				throw new Error(
					"We couldn't verify your account. Please try again or contact support@usebankole.com."
				);
			}
			setToken(receivedToken);
			setUserId(id);
			setPassword("");
			setStep("confirm");
		} catch (err: any) {
			setError(err?.message || "Something went wrong. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleConfirmDelete = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		try {
			await iamFetch("/auth/delete", "DELETE", { userId }, token);
			setToken("");
			setUserId("");
			setStep("deleted");
		} catch (err: any) {
			setError(err?.message || "Something went wrong. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const resetToSignIn = () => {
		setStep("signin");
		setToken("");
		setUserId("");
		setPassword("");
		setError("");
	};

	return (
		<div className="delete-account-page">
			<section className="bg-[#ECF2FB] flex flex-col items-center justify-center text-center py-24 md:py-32 px-6">
				<h1 className="text-4xl md:text-6xl font-SatoshiBold text-headerblue">
					Delete your account
				</h1>
				<p className="mt-4 max-w-2xl font-SatoshiRegular text-[#475467]">
					Request the permanent deletion of your Bankolé account and personal
					data. You don't need the app installed — you only need the email
					address and password of the account.
				</p>
			</section>

			<section className="max-w-5xl mx-auto px-6 py-14 md:py-20 flex flex-col md:flex-row gap-10">
				<div className="md:w-1/2 font-SatoshiRegular text-[#475467]">
					<h2 className="text-2xl font-SatoshiBold text-headerblue">
						What happens when you delete your account
					</h2>
					<ul className="mt-4 space-y-3 list-disc pl-5">
						<li>
							Your account and the personal data on your profile — such as
							your name, email address, phone number and photo — are
							permanently deleted from our active systems.
						</li>
						<li>
							Deletion is immediate once you confirm it below, and it cannot
							be undone.
						</li>
						<li>
							Projects, documents and messages you shared with workspaces or
							other users may be retained by those organisations after your
							account is deleted.
						</li>
						<li>
							We may keep limited records where the law requires it (for
							example transaction or audit records), and residual copies in
							encrypted backups are purged on our standard backup rotation.
						</li>
					</ul>

					<h2 className="mt-10 text-2xl font-SatoshiBold text-headerblue">
						Can't sign in?
					</h2>
					<p className="mt-4">
						If you've forgotten your password or lost access to the account,
						email{" "}
						<a
							href="mailto:support@usebankole.com?subject=Account%20deletion%20request"
							className="text-bblue underline"
						>
							support@usebankole.com
						</a>{" "}
						from the email address on your account and ask for your account and
						data to be deleted. We process emailed requests within 30 days.
					</p>
					<p className="mt-4">
						You can also delete your account from inside the app under{" "}
						<span className="font-SatoshiMedium text-headerblue">
							Profile &rarr; Delete account
						</span>
						.
					</p>
				</div>

				<div className="md:w-1/2">
					<div className="bg-white border border-pbg rounded-xl px-4 md:px-8 py-8">
						{step === "signin" && (
							<form className="flex flex-col gap-6" onSubmit={handleSignIn}>
								<div>
									<h2 className="text-xl font-SatoshiBold text-headerblue">
										Step 1 of 2 — Verify it's you
									</h2>
									<p className="mt-2 font-SatoshiRegular text-sm text-[#475467]">
										Sign in with your account email and password so we know
										the request really comes from you. We only use this to
										authorise the deletion — nothing is stored.
									</p>
								</div>
								<div className="flex flex-col gap-2">
									<label
										htmlFor="email"
										className="font-inter font-medium text-sm text-bblack-5"
									>
										Email address
									</label>
									<input
										type="email"
										id="email"
										name="email"
										placeholder="name@example.com"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										className={inputClass}
										autoComplete="email"
									/>
								</div>
								<div className="flex flex-col gap-2">
									<label
										htmlFor="password"
										className="font-inter font-medium text-sm text-bblack-5"
									>
										Password
									</label>
									<input
										type="password"
										id="password"
										name="password"
										placeholder="Your password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										className={inputClass}
										autoComplete="current-password"
									/>
								</div>
								{error && <p className="text-red-500 text-sm">{error}</p>}
								<button
									type="submit"
									className="py-3 rounded-lg font-SatoshiMedium bg-bblue text-white hover:bg-blueShades-6 disabled:opacity-60"
									disabled={loading}
								>
									{loading ? "Verifying..." : "Continue"}
								</button>
							</form>
						)}

						{step === "confirm" && (
							<form
								className="flex flex-col gap-6"
								onSubmit={handleConfirmDelete}
							>
								<div>
									<h2 className="text-xl font-SatoshiBold text-headerblue">
										Step 2 of 2 — Confirm deletion
									</h2>
									<p className="mt-2 font-SatoshiRegular text-sm text-[#475467]">
										You're verified as{" "}
										<span className="font-SatoshiMedium text-headerblue">
											{email}
										</span>
										. Confirming permanently deletes this account and its
										personal data — this cannot be undone.
									</p>
								</div>
								{error && <p className="text-red-500 text-sm">{error}</p>}
								<button
									type="submit"
									className="py-3 rounded-lg font-SatoshiMedium bg-bred text-white hover:opacity-90 disabled:opacity-60"
									disabled={loading}
								>
									{loading
										? "Deleting account..."
										: "Permanently delete my account"}
								</button>
								<button
									type="button"
									className="text-sm font-SatoshiRegular text-[#475467] underline"
									onClick={resetToSignIn}
									disabled={loading}
								>
									Cancel — keep my account
								</button>
							</form>
						)}

						{step === "deleted" && (
							<div className="flex flex-col gap-4 text-center py-6">
								<h2 className="text-xl font-SatoshiBold text-headerblue">
									Your account has been deleted
								</h2>
								<p className="font-SatoshiRegular text-sm text-[#475467]">
									Your account and personal data have been permanently deleted
									from our active systems. A confirmation email is on its way
									to {email}. We're sorry to see you go.
								</p>
								<p className="font-SatoshiRegular text-sm text-[#475467]">
									If this was a mistake or you have any concerns, contact{" "}
									<a
										href="mailto:support@usebankole.com"
										className="text-bblue underline"
									>
										support@usebankole.com
									</a>
									.
								</p>
							</div>
						)}
					</div>
				</div>
			</section>
		</div>
	);
};

export default DeleteAccount;
